import React, { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { supabase, uploadImage, deleteImageByUrl, sanitizeBase64Images } from '../../services/supabaseClient';
import Modal from '../Modal';
import ImageUploader from '../ImageUploader';
import { useAuth } from '../../context/AuthContext';

// Quill pesa cerca de la mitad del bundle y solo se usa dentro del modal:
// se carga bajo demanda para acelerar la carga inicial del panel.
const RichTextEditor = lazy(() => import('../RichTextEditor'));

const SLOTS = [
  { number: 1, label: 'Noticia Principal' },
  { number: 2, label: 'Noticia Secundaria 1' },
  { number: 3, label: 'Noticia Secundaria 2' },
];

const CategoriasSection = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  // form fields
  const [formData, setFormData] = useState({
    number: 1,
    title: '',
    written_by: '',
    body: '',
    category_id: '',
    image_url: '',
    fecha_publicacion: '',
    minutos_lectura: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [bodyLoading, setBodyLoading] = useState(false);

  // El input datetime-local trabaja en hora local y sin zona horaria, así que
  // hay que convertir en ambos sentidos.
  const aInputLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const desfase = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - desfase).toISOString().slice(0, 16);
  };

  const deInputLocal = (valor) => (valor ? new Date(valor).toISOString() : null);

  // Se muestra como pista lo que el sitio calcularía solo.
  const minutosEstimados = (html) => {
    if (!html) return 0;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const texto = (tmp.textContent || '').replace(/\u00a0/g, ' ').trim();
    if (!texto) return 0;
    return Math.max(1, Math.round(texto.split(/\s+/).length / 200));
  };

  const fetchCategories = useCallback(async () => {
    const { data, error } = await supabase
      .from('categorias')
      .select('id, name, color')
      .order('id');
    if (error) {
      console.error(error);
      return;
    }
    setCategories(data || []);
    if (data && data.length > 0) {
      setActiveCategoryId((prev) => prev || data[0].id);
    }
  }, []);

  const fetchNews = useCallback(async () => {
    if (!activeCategoryId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('news')
      // El listado no muestra "body"; traerlo hace descargar decenas de MB
      // (imagenes base64 incrustadas). Se carga bajo demanda al editar.
      .select('id, number, title, written_by, category_id, image_url, updated_at, fecha_publicacion, minutos_lectura')
      .eq('category_id', activeCategoryId)
      .order('updated_at', { ascending: false });
    if (error) {
      console.error(error);
    } else {
      setNews(data || []);
    }
    setLoading(false);
  }, [activeCategoryId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const openCreateModal = () => {
    setEditing(null);
    setFormData({
      number: 1,
      title: '',
      written_by: user?.name || '',
      body: '',
      category_id: activeCategoryId || '',
      image_url: '',
      fecha_publicacion: aInputLocal(new Date().toISOString()),
      minutos_lectura: '',
    });
    setImageFile(null);
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = async (item) => {
    setEditing(item);
    setFormData({
      number: item.number || 1,
      title: item.title || '',
      written_by: item.written_by || '',
      body: item.body || '',
      category_id: item.category_id || activeCategoryId,
      image_url: item.image_url || '',
      fecha_publicacion: aInputLocal(item.fecha_publicacion || item.updated_at || item.created_at),
      minutos_lectura: item.minutos_lectura ?? '',
    });
    setImageFile(null);
    setFormError('');
    setShowModal(true);

    // El "body" no viene en el listado: se pide solo para la noticia abierta.
    if (item.body === undefined) {
      setBodyLoading(true);
      const { data, error } = await supabase
        .from('news')
        .select('body')
        .eq('id', item.id)
        .maybeSingle();
      if (error) {
        setFormError('No se pudo cargar el cuerpo de la noticia: ' + error.message);
      } else {
        setFormData((prev) => ({ ...prev, body: data?.body || '' }));
      }
      setBodyLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar la noticia "${item.title}"?`)) return;
    const { error } = await supabase.from('news').delete().eq('id', item.id);
    if (error) {
      alert('Error al eliminar: ' + error.message);
      return;
    }
    if (item.image_url) await deleteImageByUrl(item.image_url);
    fetchNews();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (bodyLoading) return; // aun se esta descargando el cuerpo original
    setFormError('');
    setSaving(true);

    try {
      let imageUrl = formData.image_url;
      if (imageFile) {
        if (editing && editing.image_url) await deleteImageByUrl(editing.image_url);
        imageUrl = await uploadImage(imageFile);
      } else if (imageFile === null && formData.image_url === '') {
        if (editing && editing.image_url) await deleteImageByUrl(editing.image_url);
        imageUrl = null;
      }

      // Nunca guardar imagenes base64 incrustadas: se suben al bucket primero.
      const bodyLimpio = await sanitizeBase64Images(formData.body);

      const payload = {
        number: parseInt(formData.number, 10),
        title: formData.title,
        written_by: formData.written_by,
        body: bodyLimpio,
        category_id: parseInt(formData.category_id, 10),
        image_url: imageUrl,
        fecha_publicacion: deInputLocal(formData.fecha_publicacion),
        // Vacío significa "que lo calcule el sitio a partir del texto".
        minutos_lectura: formData.minutos_lectura === '' ? null : Number(formData.minutos_lectura),
        updated_at: new Date().toISOString(),
      };

      if (editing) {
        const { error } = await supabase.from('news').update(payload).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('news').insert([payload]);
        if (error) throw error;
      }

      setShowModal(false);
      fetchNews();
    } catch (err) {
      setFormError(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const getCategory = (id) => categories.find((c) => c.id === id);
  const activeCategory = getCategory(activeCategoryId);

  const renderTable = (slot) => {
    const items = news.filter((n) => n.number === slot.number);
    return (
      <div key={slot.number}>
        <h3 className="section-title">{slot.label}</h3>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Acciones</th>
                <th>Foto</th>
                <th>Número</th>
                <th>Título</th>
                <th>Escrito por</th>
                <th>Categoría</th>
                <th>Fecha</th>
                <th>Lectura</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-row">
                    Sin noticias en este espacio
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const cat = getCategory(item.category_id);
                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="actions-cell">
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openEditModal(item)}
                          >
                            Editar
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(item)}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                      <td>
                        {item.image_url ? (
                          <img src={item.image_url} alt="" className="table-image" loading="lazy" decoding="async" />
                        ) : (
                          <div className="no-image">Sin foto</div>
                        )}
                      </td>
                      <td>{item.number}</td>
                      <td>{item.title}</td>
                      <td>{item.written_by}</td>
                      <td>
                        {cat && (
                          <span
                            className="category-badge"
                            style={{ background: cat.color || '#2d6a2d' }}
                          >
                            {cat.name}
                          </span>
                        )}
                      </td>
                      <td>
                        {item.fecha_publicacion || item.updated_at
                          ? new Date(item.fecha_publicacion || item.updated_at).toLocaleDateString('es-PE')
                          : '-'}
                      </td>
                      <td>
                        {item.minutos_lectura
                          ? `${item.minutos_lectura} min`
                          : <span style={{ color: '#999' }}>Automático</span>}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="subtabs">
        {categories.map((cat) => (
          <button
            key={cat.id}
            className={`subtab ${activeCategoryId === cat.id ? 'active' : ''}`}
            onClick={() => setActiveCategoryId(cat.id)}
            style={
              activeCategoryId === cat.id
                ? { background: cat.color || '#2d6a2d', borderColor: cat.color || '#2d6a2d' }
                : { borderColor: cat.color || '#2d6a2d', color: cat.color || '#2d6a2d' }
            }
          >
            {cat.name}
          </button>
        ))}
      </div>

      {activeCategory && (
        <div className="add-button-container">
          <button className="btn btn-primary" onClick={openCreateModal}>
            + Agregar Noticia
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        SLOTS.map((slot) => renderTable(slot))
      )}

      {showModal && (
        <Modal
          title={editing ? 'Editar Noticia' : 'Agregar Noticia'}
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={saving || bodyLoading}
              >
                {saving ? 'Guardando...' : bodyLoading ? 'Cargando...' : 'Guardar'}
              </button>
            </>
          }
        >
          <form onSubmit={handleSubmit}>
            {formError && <div className="error-message">{formError}</div>}

            <div className="form-row">
              <div className="form-group">
                <label>Número / Espacio *</label>
                <select
                  value={formData.number}
                  onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                  required
                >
                  <option value={1}>1 - Noticia Principal</option>
                  <option value={2}>2 - Noticia Secundaria 1</option>
                  <option value={3}>3 - Noticia Secundaria 2</option>
                </select>
              </div>

              <div className="form-group">
                <label>Categoría *</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  required
                >
                  <option value="">Seleccione una categoría</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Título *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Escrito por *</label>
              <input
                type="text"
                value={formData.written_by}
                onChange={(e) => setFormData({ ...formData, written_by: e.target.value })}
                required
              />
            </div>

            <Suspense
              fallback={
                <div className="form-group">
                  <label>Cuerpo</label>
                  <div className="loading">
                    <div className="spinner"></div>
                  </div>
                </div>
              }
            >
              <div className="form-row">
              <div className="form-group">
                <label>Fecha de publicación</label>
                <input
                  type="datetime-local"
                  value={formData.fecha_publicacion}
                  onChange={(e) => setFormData({ ...formData, fecha_publicacion: e.target.value })}
                />
                <small style={{ color: '#666', fontSize: '0.85em' }}>
                  Es la fecha que se muestra en el sitio. Corregir la nota más
                  adelante no la modifica.
                </small>
              </div>

              <div className="form-group">
                <label>Minutos de lectura</label>
                <input
                  type="number"
                  min="1"
                  value={formData.minutos_lectura}
                  onChange={(e) => setFormData({ ...formData, minutos_lectura: e.target.value })}
                  placeholder={`Automático: ${minutosEstimados(formData.body)} min`}
                />
                <small style={{ color: '#666', fontSize: '0.85em' }}>
                  Si se deja vacío, el sitio lo calcula del texto.
                </small>
              </div>
            </div>

            <RichTextEditor
                value={formData.body}
                onChange={(v) => setFormData({ ...formData, body: v })}
              />
            </Suspense>

            <ImageUploader
              currentImageUrl={formData.image_url}
              onFileSelect={(file) => {
                setImageFile(file);
                if (file === null) setFormData({ ...formData, image_url: '' });
              }}
              label="Imagen de portada"
            />
          </form>
        </Modal>
      )}
    </div>
  );
};

export default CategoriasSection;
